/* ==========================================================================
   THE HOME-PAGE ENGRAVING PICKER

   Case, then phone, then color, then go. Everything it knows comes from the
   JSON the template wrote, and every lookup is by INDEX rather than by the
   text of an option - because option values are translated per market, and a
   picker that matches on the word "Black" works in English and quietly breaks
   in every other language.

   The map the template writes holds only variants that are actually in stock,
   so it doubles as the availability table: a phone with no entry at all is not
   offered, and a color the chosen phone is not made in is shown struck through
   rather than silently dropped when the button is pressed.

   The phone is chosen through the product page's own selector - the chip and
   the list, not a native select - so the two pages offer the same control. The
   list is moved to <body> before it is shown, because the card around it
   carries a backdrop-filter and a filtered ancestor becomes the containing
   block for anything positioned fixed inside it.

   BUILD ONCE, MARK AFTERWARDS. Every row here is created a single time and
   then only has classes and attributes flipped on it. Rebuilding the five case
   swatches and the six color swatches on every press - each one a fresh <img>
   with a fresh src - measured 27 to 60ms of main thread per interaction, all
   of it spent recreating markup that had not changed. Only the phone list is
   genuinely rebuilt, and only when the case changes, because a different case
   offers a different set of phones.
   ========================================================================== */
(function () {
  "use strict";

  var MOBILE = "(max-width: 749px)";
  function isMobile() { return window.matchMedia(MOBILE).matches; }

  function init(root) {
    if (root.__gcPzcReady) return;
    root.__gcPzcReady = true;

    var dataEl = root.querySelector("[data-gc-pzc-data]");
    var cases;
    try { cases = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!cases || !cases.length) { root.hidden = true; return; }

    var elCases = root.querySelector("[data-gc-pzc-cases]");
    var elDevice = root.querySelector("[data-gc-pzc-device]");
    var elDeviceValue = root.querySelector("[data-gc-pzc-device-value]");
    var elPop = root.querySelector("[data-gc-pzc-pop]");
    var elPopList = root.querySelector("[data-gc-pzc-pop-list]");
    var elScrim = root.querySelector("[data-gc-pzc-pop-scrim]");
    var elColors = root.querySelector("[data-gc-pzc-colors]");
    var elColorLabel = root.querySelector("[data-gc-pzc-color-label]");
    var elImg = root.querySelector("[data-gc-pzc-img]");
    var elGo = root.querySelector("[data-gc-pzc-go]");
    var elMono = root.querySelector("[data-gc-pzc-mono]");
    var elInit = root.querySelector("[data-gc-pzc-initials]");
    var elTiers = root.querySelector("[data-gc-pzc-tiers]");
    var elVid = root.querySelector("[data-gc-pzc-vid]");
    var elVidEl = root.querySelector("[data-gc-pzc-vid-el]");
    var elImgLink = root.querySelector("[data-gc-pzc-imglink]");
    if (!elCases || !elDevice || !elPop || !elPopList || !elColors || !elGo) return;

    var soldOut = root.getAttribute("data-gc-pzc-sold") || "Sold out";
    /* Replaced once the film is set up. A no-op until then, so the case and
       colour handlers can call it from the moment they are wired. */
    var engaged = function () {};

    /* out of the filtered card, so the sheet is positioned against the
       viewport rather than against the card */
    if (elPop.parentElement !== document.body) document.body.appendChild(elPop);
    if (elScrim && elScrim.parentElement !== document.body) document.body.appendChild(elScrim);

    var ci = 0;   /* case index */
    var di = 0;   /* device index, into this case's own d[] */
    var ki = 0;   /* color index, into this case's own c[] */
    var builtColorsFor = -1;   /* which case the color row currently shows */

    function current() { return cases[ci]; }

    /* the map is keyed "device:color" and holds only in-stock pairings */
    function pairing(c, d, k) { return (c.v && c.v[d + ":" + k]) || null; }
    function deviceLive(c, d) {
      var n = Math.max(1, (c.c || []).length);
      for (var k = 0; k < n; k++) { if (pairing(c, d, k)) return true; }
      return false;
    }

    function preview() {
      var c = current();
      var src = (c.c && c.c[ki] && c.c[ki].i) || c.f || "";
      if (src && elImg.getAttribute("src") !== src) {
        elImg.setAttribute("src", src);
        elImg.setAttribute("alt", c.t || "");
      }
      /* Outside the guard above: the picture does not change when only the
         PHONE changes, but the variant behind it does, so the link would
         otherwise still point at the previous handset. */
      if (elImgLink) {
        var id = pairing(c, di, ki);
        var url = c.u + (id ? (c.u.indexOf("?") === -1 ? "?" : "&") + "variant=" + encodeURIComponent(id) : "");
        elImgLink.setAttribute("href", url);
        /* the product name is the link text - nothing else needs saying */
        elImgLink.setAttribute("aria-label", c.t || "");
        elImgLink.setAttribute("title", c.t || "");
      }
    }

    /* ------------------------------------------------------------- CASES */
    function buildCases() {
      elCases.innerHTML = "";
      cases.forEach(function (c, n) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "gc-pzc__case";
        b.setAttribute("role", "radio");
        b.title = c.t || "";
        b.setAttribute("aria-label", c.t || "");
        var im = document.createElement("img");
        im.src = (c.c && c.c[0] && (c.c[0].s || c.c[0].i)) || c.f || "";
        im.alt = "";
        im.loading = "lazy";
        im.decoding = "async";
        b.appendChild(im);
        b.addEventListener("click", function () {
          /* Before the early return, not after. Pressing the case that is
             already chosen is still the shopper saying "show me this one" -
             it was the one press that left the film sitting over the preview,
             so the only way past it was to pick something you did not want. */
          engaged();
          if (ci === n) return;
          /* Keep the chosen phone if the new case also offers it. It is
             carried over by NAME, because the same phone sits at a different
             index on a case with a different line-up. */
          var keep = currentDeviceName();
          ci = n;
          ki = 0;
          var match = current().d.indexOf(keep);
          di = match >= 0 ? match : 0;
          markCases();
          buildDevices();
          buildColors();
          preview();
          engaged();
        });
        elCases.appendChild(b);
      });
      markCases();
    }

    function markCases() {
      var b = elCases.children;
      for (var i = 0; i < b.length; i++) {
        var on = i === ci;
        b[i].classList.toggle("is-on", on);
        b[i].setAttribute("aria-checked", on ? "true" : "false");
      }
    }

    /* ------------------------------------------------------------ PHONES */
    function currentDeviceName() {
      var c = current();
      return (c.d && c.d[di]) || "";
    }

    function buildDevices() {
      var c = current();
      elPopList.innerHTML = "";
      var firstLive = -1;
      (c.d || []).forEach(function (name, n) {
        /* A phone with nothing in stock on this case is not listed at all.
           Offering it costs the shopper two further choices and then lands
           them on a page that cannot sell them what they picked. */
        if (!deviceLive(c, n)) return;
        if (firstLive < 0) firstLive = n;
        var b = document.createElement("button");
        b.type = "button";
        b.className = "Popover__Value Heading Link Link--primary u-h6";
        b.setAttribute("role", "option");
        b.setAttribute("data-di", String(n));
        b.textContent = name;
        b.addEventListener("click", function () {
          di = n;
          markDevices();
          syncDeviceLabel();
          markColors();
          preview();
          closePop(true);
        });
        elPopList.appendChild(b);
      });
      if (!deviceLive(c, di)) di = firstLive < 0 ? 0 : firstLive;
      markDevices();
      syncDeviceLabel();
      /* the shared grouper cannot see this list at load time - it is built
         from JSON right here - so it is told once the options exist */
      if (typeof window.gcGroupPopovers === "function") {
        elPopList.removeAttribute("data-gc-grouped");
        window.gcGroupPopovers();
      }
    }

    function markDevices() {
      var opts = elPopList.children;
      for (var i = 0; i < opts.length; i++) {
        var on = Number(opts[i].getAttribute("data-di")) === di;
        opts[i].classList.toggle("is-selected", on);
        opts[i].setAttribute("aria-selected", on ? "true" : "false");
      }
    }

    function syncDeviceLabel() {
      if (elDeviceValue) elDeviceValue.textContent = currentDeviceName();
    }

    /* ------------------------------------------------------- THE LIST BOX */
    var popOpen = false;

    function place() {
      if (!popOpen) return;
      if (isMobile()) {
        elPop.style.top = "";
        elPop.style.left = "";
        elPop.style.width = "";
        return;
      }
      var r = elDevice.getBoundingClientRect();
      var h = elPop.offsetHeight || 320;
      var below = window.innerHeight - r.bottom - 12;
      var above = r.top - 12;
      elPop.style.width = Math.round(r.width) + "px";
      elPop.style.left = Math.round(r.left) + "px";
      /* flip above the chip when the space under it cannot hold the list */
      elPop.style.top = (below < h && above > below)
        ? Math.round(Math.max(8, r.top - h - 6)) + "px"
        : Math.round(r.bottom + 6) + "px";
    }

    function openPop() {
      if (popOpen) return;
      popOpen = true;
      elPop.hidden = false;
      if (elScrim) elScrim.hidden = false;
      elPop.setAttribute("aria-hidden", "false");
      elDevice.setAttribute("aria-expanded", "true");
      /* One measure, one flush. place() already reads offsetHeight, and the
         open state changes only opacity and transform - neither of which moves
         the box - so measuring before the class goes on is measuring the same
         box. Reading it again afterwards was a second forced layout for
         nothing. The read itself is what gives the transition a start value;
         requestAnimationFrame would too, but it is paused in a background tab,
         where the list would then never appear. */
      place();
      elPop.classList.add("is-open");
      if (elScrim) elScrim.classList.add("is-open");
      var sel = elPopList.querySelector(".is-selected") || elPopList.firstElementChild;
      if (sel) { sel.focus(); scrollIntoList(sel); }
      document.addEventListener("keydown", onKey, true);
      document.addEventListener("mousedown", onOutside, true);
      window.addEventListener("resize", place);
      window.addEventListener("scroll", place, true);
    }

    function closePop(focusBack) {
      if (!popOpen) return;
      popOpen = false;
      elPop.classList.remove("is-open");
      if (elScrim) elScrim.classList.remove("is-open");
      elPop.setAttribute("aria-hidden", "true");
      elDevice.setAttribute("aria-expanded", "false");
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onOutside, true);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      /* kept in the layer until the transition has run, so it does not vanish
         mid-slide on a phone */
      window.setTimeout(function () {
        if (popOpen) return;
        elPop.hidden = true;
        if (elScrim) elScrim.hidden = true;
      }, 240);
      if (focusBack) elDevice.focus();
    }

    function scrollIntoList(el) {
      var box = elPopList.getBoundingClientRect();
      var r = el.getBoundingClientRect();
      if (r.top < box.top) elPopList.scrollTop -= (box.top - r.top);
      else if (r.bottom > box.bottom) elPopList.scrollTop += (r.bottom - box.bottom);
    }

    function move(step) {
      var opts = [].slice.call(elPopList.children);
      if (!opts.length) return;
      var at = opts.indexOf(document.activeElement);
      var next = at < 0 ? 0 : Math.min(opts.length - 1, Math.max(0, at + step));
      opts[next].focus();
      scrollIntoList(opts[next]);
    }

    function onKey(e) {
      if (e.key === "Escape") { e.preventDefault(); closePop(true); return; }
      if (e.key === "ArrowDown") { e.preventDefault(); move(1); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); move(-1); return; }
      if (e.key === "Home") { e.preventDefault(); move(-9999); return; }
      if (e.key === "End") { e.preventDefault(); move(9999); return; }
      if (e.key === "Tab") { closePop(true); }
    }

    function onOutside(e) {
      if (elPop.contains(e.target) || elDevice.contains(e.target)) return;
      closePop(false);
    }

    elDevice.addEventListener("click", function () {
      if (popOpen) closePop(true); else openPop();
    });
    elDevice.addEventListener("keydown", function (e) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); openPop(); }
    });
    var popClose = elPop.querySelector("[data-gc-pzc-pop-close]");
    if (popClose) popClose.addEventListener("click", function () { closePop(true); });
    if (elScrim) elScrim.addEventListener("click", function () { closePop(false); });

    /* ------------------------------------------------------------ COLORS */
    /* The swatches are rebuilt only when the CASE changes, because that is the
       only thing that changes which colors exist. Choosing a phone changes
       which of them can be bought, and choosing a color changes which one is
       ticked - both of those are marking, not building. */
    function buildColors() {
      var c = current();
      if (builtColorsFor === ci) { markColors(); return; }
      builtColorsFor = ci;
      var list = c.c || [];
      elColors.innerHTML = "";
      list.forEach(function (col, n) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "gc-pzc__color";
        b.setAttribute("role", "radio");
        var im = document.createElement("img");
        im.src = col.s || col.i;
        im.alt = "";
        im.loading = "lazy";
        im.decoding = "async";
        b.appendChild(im);
        b.addEventListener("click", function () {
          /* asked fresh: whether this color can be bought depends on the phone
             chosen since, not on how things stood when the row was built */
          if (!pairing(current(), di, n)) return;
          ki = n;
          markColors();
          preview();
          engaged();
        });
        elColors.appendChild(b);
      });
      markColors();
    }

    function markColors() {
      var c = current();
      var list = c.c || [];
      /* a case with one color has nothing to choose, so the row is hidden
         rather than shown as a single dead swatch */
      var only = list.length < 2;
      if (elColorLabel) elColorLabel.hidden = only;
      elColors.hidden = only;

      /* If the chosen phone is not made in the chosen color, move to one it is
         made in, rather than leaving a selection the button would have to
         quietly drop. */
      if (!only && !pairing(c, di, ki)) {
        for (var i = 0; i < list.length; i++) {
          if (pairing(c, di, i)) { ki = i; break; }
        }
      }

      var b = elColors.children;
      for (var j = 0; j < b.length; j++) {
        var live = !!pairing(c, di, j);
        var on = j === ki;
        b[j].classList.toggle("is-on", on);
        b[j].classList.toggle("is-off", !live);
        b[j].disabled = !live;
        b[j].setAttribute("aria-checked", on ? "true" : "false");
        var nm = (list[j] && list[j].n) || "";
        var label = live ? nm : nm + " – " + soldOut;
        b[j].title = label;
        b[j].setAttribute("aria-label", label);
      }
    }

    /* ------------------------------------------------------- THE LETTERS */
    /* Only what can actually be stamped: the workshop engraves A-Z and 0-9,
       so anything else is dropped as it is typed rather than accepted here and
       refused later. The same rule the product page applies. */
    /* the field's maxlength is the top of the fee ladder (rendered by the snippet) */
    function maxc() { var m = elInit ? Number(elInit.getAttribute("maxlength")) : 0; return m > 0 ? m : 5; }
    function clean(v) { return String(v || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, maxc()); }
    function initials() { return elInit ? clean(elInit.value) : ""; }

    function paintLetters() {
      var v = initials();
      if (elMono) {
        /* Empty shows a faded AB - a hint at what the space is for, not a
           claim that those are their letters. */
        elMono.textContent = v || "AB";
        elMono.style.setProperty("--gc-ini-scale", v.length > 6 ? (6 / v.length).toFixed(3) : "1");
        elMono.classList.toggle("is-ghost", !v);
      }
      markTiers(v.length);
    }

    /* the fee variant for a given length, read from the ladder the template
       rendered - ids come from the fee product itself, so they stay right if
       the shop ever re-creates those variants */
    function feeVariant(len) {
      if (!elTiers) return null;
      var t = elTiers.children;
      for (var i = 0; i < t.length; i++) {
        if (len <= Number(t[i].getAttribute("data-gc-pzc-tier"))) {
          return Number(t[i].getAttribute("data-gc-pzc-tier-id")) || null;
        }
      }
      return t.length ? (Number(t[t.length - 1].getAttribute("data-gc-pzc-tier-id")) || null) : null;
    }

    /* which rung of the ladder the current length is on */
    function markTiers(len) {
      if (!elTiers) return;
      var t = elTiers.children;
      var pick = -1;
      for (var i = 0; i < t.length; i++) {
        if (pick < 0 && len > 0 && len <= Number(t[i].getAttribute("data-gc-pzc-tier"))) pick = i;
      }
      for (var j = 0; j < t.length; j++) t[j].classList.toggle("is-on", j === pick);
    }

    if (elInit) {
      elInit.addEventListener("input", function () {
        var v = clean(elInit.value);
        if (elInit.value !== v) elInit.value = v;
        paintLetters();
        engaged();
      });
      elInit.addEventListener("focus", function () { engaged(); });
    }

    /* ---------------------------------------------------------------- GO */
    var busy = false;
    elGo.addEventListener("click", function () {
      if (busy) return;
      var v = initials();
      /* Nothing to engrave yet. The button is the prompt, so it puts the
         cursor where the letters go rather than adding a bare case to the cart
         under a label that says Engrave. */
      if (!v) {
        if (elInit) {
          elInit.focus();
          root.classList.add("is-asking");
          window.setTimeout(function () { root.classList.remove("is-asking"); }, 1200);
        }
        return;
      }

      var c = current();
      var id = pairing(c, di, ki);
      if (!id) return;

      /* The two lines the rest of the shop expects: the case carrying the
         engraving, and the fee variant for that length, linked by one id.
         This is the payload the product page drawer sends, matched key for
         key, because the cart, the undo toast and checkout all read them. */
      var feeId = feeVariant(v.length);
      /* GC_VARIANT lives in the product page drawer bundle, which the home
         page does not load - measured: undefined here. The ladder rendered in
         step three already carries the real variant id for each length,
         straight from the fee product, so it answers this without needing
         that bundle and without a variant id written into the script. */
      var fallback = c.u + (c.u.indexOf("?") === -1 ? "?" : "&") + "variant=" + encodeURIComponent(id) + "&personalize=1";
      if (!feeId) { window.location.assign(fallback); return; }
      var pid = "gcpzc_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8);

      busy = true;
      root.classList.add("is-adding");
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ items: [
          { id: id,    quantity: 1, properties: { Personalization: v, _personalization_id: pid } },
          { id: feeId, quantity: 1, properties: { For: v, _personalization_link: pid } }
        ] })
      }).then(function (r) {
        if (!r.ok) throw new Error("add failed");
        return r.json();
      }).then(function () {
        if (typeof window.refreshAndOpenCartDrawer === "function") window.refreshAndOpenCartDrawer();
        else if (typeof window.openCartDrawer === "function") window.openCartDrawer();
        else window.location.assign("/cart");
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      }).catch(function () {
        /* the product page can always finish what this could not */
        window.location.assign(fallback);
      }).then(function () {
        busy = false;
        root.classList.remove("is-adding");
      });
    });

    /* The label cycles: engrave, personalize, add initials, add monogram. All
       four are already in the markup stacked in one grid cell, so the button
       was laid out at the width of the longest from the first paint and this
       moves nothing. It runs only while the button is on screen and the tab is
       visible, holds while the shopper is hovering or focused on it, and does
       not run at all for anyone who asked for reduced motion. */
    /* Same reasoning as the set tile: this is not gated on reduced motion,
       because the guard removed the label change entirely on any machine with
       animations turned off. The stylesheet drops the crossfade to an instant
       swap under that setting instead. */
    var labels = [].slice.call(elGo.querySelectorAll(".gc-pzc__golabel"));
    if (labels.length > 1) {
      var at = 0, held = false;

      /* On screen is answered by measuring the button, not by an
         IntersectionObserver. The observer callback arrives asynchronously and
         its first answer here was "not intersecting", which stopped the timer
         that had just started - so the label never changed. A rect read every
         few seconds costs nothing and cannot get stuck. */
      function inView() {
        var r = elGo.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) return false;
        return r.bottom > -80 && r.top < (window.innerHeight || 0) + 80;
      }
      function tick() {
        if (held || document.hidden || !inView()) return;
        labels[at].classList.remove("is-on");
        at = (at + 1) % labels.length;
        labels[at].classList.add("is-on");
      }
      elGo.addEventListener("mouseenter", function () { held = true; });
      elGo.addEventListener("mouseleave", function () { held = false; });
      elGo.addEventListener("focus", function () { held = true; });
      elGo.addEventListener("blur", function () { held = false; });
      window.setInterval(tick, 2800);
    }

    /* ---------------------------------------------------------- THE FILM */
    /* Shown until the shopper has something of their own on the case - a
       letter typed, a colour or a case picked. Offered only when the player is
       actually on the page to open, and never on a metered connection. */
    var filmOn = false;
    function metered() {
      var n = navigator.connection || {};
      return n.saveData === true ||
        (typeof n.effectiveType === "string" && /(^|-)2g$|^3g$/.test(n.effectiveType));
    }
    function showFilm() {
      if (!elVid || !elVidEl || metered()) return;
      if (!document.querySelector("[data-gc-pvid-modal]")) return;
      var api = window.gcPersonalizeVideo;
      if (!api) return;
      /* The 480p rendition is right for a 76px chip on the product page. Here
         the film fills half the card - measured at 375px, and 500px on a wide
         screen, which on a 2x display is asking a 480-wide source to cover
         1000 device pixels. Above the phone breakpoint it takes the 720p. */
      var big = (window.innerWidth || 0) >= 750;
      var src = (big && api.hd) ? api.hd : api.sd;
      if (!src) return;
      if (!elVidEl.getAttribute("src")) {
        /* Poster FIRST. It is what the element paints before the first frame
           is decoded and whenever playback is refused, and without it the
           square is black - which is what was on screen. */
        if (api.poster) elVidEl.setAttribute("poster", api.poster);
        elVidEl.src = src;
        /* Rewound by hand rather than looping, so only the opening seconds of
           the rendition are ever fetched. */
        elVidEl.addEventListener("timeupdate", function () {
          if (elVidEl.currentTime < 5) return;
          try { elVidEl.currentTime = 0; } catch (e) {}
        });
        elVidEl.addEventListener("playing", function () { elVid.classList.add("is-playing"); }, { once: true });
      }
      elVid.hidden = false;
      filmOn = true;
      tryPlay();
    }

    /* Autoplay is refused on a hidden tab and inside the theme editor frame.
       The poster carries the moment either way, and the first touch anywhere
       on the page is a user gesture - which is exactly what the browser was
       waiting for, so it is worth asking once more then. */
    function tryPlay() {
      if (!elVidEl) return;
      var p = elVidEl.play();
      if (p && p.catch) p.catch(function () {
        document.addEventListener("pointerdown", function once() {
          document.removeEventListener("pointerdown", once, true);
          if (filmOn && elVidEl) { var q = elVidEl.play(); if (q && q.catch) q.catch(function () {}); }
        }, true);
      });
    }
    function hideFilm() {
      if (!elVid) return;
      filmOn = false;
      elVid.hidden = true;
      if (elVidEl) { try { elVidEl.pause(); } catch (e) {} }
    }
    engaged = function () { if (filmOn) hideFilm(); };

    if (elVid) {
      document.addEventListener("visibilitychange", function () {
        if (!elVidEl) return;
        if (document.hidden) { try { elVidEl.pause(); } catch (e) {} }
        else if (filmOn) { var q = elVidEl.play(); if (q && q.catch) q.catch(function () {}); }
      });
      if ("IntersectionObserver" in window) {
        new IntersectionObserver(function (entries) {
          if (!filmOn || !elVidEl) return;
          if (entries[entries.length - 1].isIntersecting) {
            var q = elVidEl.play(); if (q && q.catch) q.catch(function () {});
          } else {
            try { elVidEl.pause(); } catch (e) {}
          }
        }, { rootMargin: "80px" }).observe(elVid);
      }
    }

    buildCases();
    buildDevices();
    buildColors();
    preview();
    paintLetters();
    /* the player script runs deferred too, so the rendition may not be
       published yet on the first pass */
    showFilm();
    if (!filmOn) window.setTimeout(showFilm, 400);
  }

  function boot() {
    [].slice.call(document.querySelectorAll("[data-gc-pzc]")).forEach(init);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
